import type { ErrorCode } from './error-codes.js'
import { ErrorCodes } from './error-codes.js'

/**
 * Parameters for creating an AppError with i18n support
 */
export interface AppErrorParams {
  /** Error code for i18n translation (e.g., 'AUTH_INVALID_CREDENTIALS') */
  code: ErrorCode
  /** Fallback message in English (used if frontend doesn't have translation) */
  message?: string
  /** Interpolation values for the translated message (e.g., { resource: 'User' }) */
  interpolation?: Record<string, string | number>
}

/**
 * Base application error with i18n support.
 *
 * Can be created with:
 * - String message (legacy, backward compatible)
 * - AppErrorParams object (new, with code and interpolation)
 *
 * @example
 * // Legacy usage
 * throw new AppError('Something went wrong', 400)
 *
 * // New usage with i18n
 * throw new AppError({
 *   code: ErrorCodes.USER_NOT_FOUND,
 *   message: 'User not found',
 *   interpolation: { resource: 'User' }
 * }, 404)
 */
export class AppError extends Error {
  public readonly statusCode: number
  public readonly code: ErrorCode
  public readonly interpolation?: Record<string, string | number>
  public readonly details?: unknown

  constructor(
    params: AppErrorParams | string,
    statusCode: number = 400,
    details?: unknown
  ) {
    if (typeof params === 'string') {
      // Legacy: string message (backward compatible)
      super(params)
      this.code = ErrorCodes.SYSTEM_INTERNAL_ERROR
    } else {
      // New: structured params with code
      super(params.message || params.code)
      this.code = params.code
      this.interpolation = params.interpolation
    }

    this.statusCode = statusCode
    this.details = details
    this.name = 'AppError'
    Error.captureStackTrace(this, this.constructor)
  }
}

/**
 * Resource not found error (404)
 */
export class NotFoundError extends AppError {
  constructor(resource: string = 'Resource') {
    super({
      code: ErrorCodes.RESOURCE_NOT_FOUND,
      message: `${resource} not found`,
      interpolation: { resource }
    }, 404)
    this.name = 'NotFoundError'
  }
}

/**
 * Authentication required error (401)
 */
export class UnauthorizedError extends AppError {
  constructor(codeOrMessage: ErrorCode | string = ErrorCodes.AUTH_TOKEN_REQUIRED, message?: string) {
    // Check if first param is an ErrorCode
    const isCode = Object.values(ErrorCodes).includes(codeOrMessage as ErrorCode)

    if (isCode) {
      super({
        code: codeOrMessage as ErrorCode,
        message: message || 'Unauthorized'
      }, 401)
    } else {
      // Legacy: string message
      super({
        code: ErrorCodes.AUTH_TOKEN_REQUIRED,
        message: codeOrMessage as string
      }, 401)
    }
    this.name = 'UnauthorizedError'
  }
}

/**
 * Access denied error (403)
 */
export class ForbiddenError extends AppError {
  constructor(codeOrMessage: ErrorCode | string = ErrorCodes.PERMISSION_DENIED, message?: string) {
    const isCode = Object.values(ErrorCodes).includes(codeOrMessage as ErrorCode)

    if (isCode) {
      super({
        code: codeOrMessage as ErrorCode,
        message: message || 'Access denied'
      }, 403)
    } else {
      super({
        code: ErrorCodes.PERMISSION_DENIED,
        message: codeOrMessage as string
      }, 403)
    }
    this.name = 'ForbiddenError'
  }
}

/**
 * Resource conflict error (409)
 */
export class ConflictError extends AppError {
  constructor(codeOrMessage: ErrorCode | string = ErrorCodes.RESOURCE_CONFLICT, message?: string) {
    const isCode = Object.values(ErrorCodes).includes(codeOrMessage as ErrorCode)

    if (isCode) {
      super({
        code: codeOrMessage as ErrorCode,
        message: message || 'Conflict'
      }, 409)
    } else {
      super({
        code: ErrorCodes.RESOURCE_CONFLICT,
        message: codeOrMessage as string
      }, 409)
    }
    this.name = 'ConflictError'
  }
}

/**
 * Standard validation error detail
 */
export interface ValidationDetail {
  path: string
  message: string
  /** Error code for i18n translation */
  code?: string
  /** Interpolation values */
  interpolation?: Record<string, string | number>
}

/**
 * Validation error with field-level details (400)
 */
export class ValidationError extends AppError {
  public override readonly details: ValidationDetail[]

  constructor(messageOrCode: string | ErrorCode = ErrorCodes.VALIDATION_ERROR, details: ValidationDetail[] = []) {
    const isCode = Object.values(ErrorCodes).includes(messageOrCode as ErrorCode)

    if (isCode) {
      super({
        code: messageOrCode as ErrorCode,
        message: 'Validation error'
      }, 400)
    } else {
      super({
        code: ErrorCodes.VALIDATION_ERROR,
        message: messageOrCode as string
      }, 400)
    }

    this.name = 'ValidationError'
    this.details = details
  }
}

// Re-export ErrorCodes for convenience
export { ErrorCodes } from './error-codes.js'
export type { ErrorCode } from './error-codes.js'
